import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle,
  Users,
  LayoutDashboard,
  History,
  Mic2,
  Sparkles,
  X,
  ChevronRight,
  Lightbulb,
  Zap,
  RefreshCw,
} from 'lucide-react';
import { OptimizationResult, JobData } from './shared/types';

export type NextActionPage =
  | 'prepare-for-interview'
  | 'ai-companion'
  | 'collaborate'
  | 'dashboard'
  | 'history'
  | 'optimize-another';

interface NextActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAction: (page: NextActionPage) => void;
  optimizationResult: OptimizationResult | null;
  jobData: JobData | null;
}


/** Suggested next step based on flow (e.g. just applied → prepare for interview). */
function getSuggestion(jobTitle?: string): { title: string; body: string; recommendedId: NextActionPage } {
  if (jobTitle) {
    return {
      title: 'You’re one step closer',
      body: 'Prepare for interviews so you’re ready when they call. Our AI can help you practice answers for this role.',
      recommendedId: 'prepare-for-interview',
    };
  }
  return {
    title: 'What’s next?',
    body: 'Talk to your AI companion — it already knows about your resume and this optimization. Get tailored advice.',
    recommendedId: 'ai-companion',
  };
}

const NextActionModal: React.FC<NextActionModalProps> = ({
  isOpen,
  onClose,
  onAction,
  optimizationResult,
  jobData,
}) => {
  const { t } = useTranslation();
  const actions = React.useMemo(() => [
    { id: 'prepare-for-interview' as const, label: t('optimizer.nextActionModal.prepareForInterview'), description: t('optimizer.nextActionModal.prepareDesc'), icon: Mic2 },
    { id: 'ai-companion' as const, label: t('optimizer.nextActionModal.aiCompanion'), description: t('optimizer.nextActionModal.aiCompanionDesc'), icon: MessageCircle },
    { id: 'optimize-another' as const, label: 'Optimize Another Job', description: 'Start fresh with a new resume & job target', icon: RefreshCw },
    { id: 'collaborate' as const, label: t('optimizer.nextActionModal.collaborate'), description: t('optimizer.nextActionModal.collaborateDesc'), icon: Users },
    { id: 'dashboard' as const, label: t('optimizer.nextActionModal.goToDashboard'), description: t('optimizer.nextActionModal.goToDashboardDesc'), icon: LayoutDashboard },
    { id: 'history' as const, label: t('optimizer.nextActionModal.myActivities'), description: t('optimizer.nextActionModal.myActivitiesDesc'), icon: History },
  ], [t]);
  const suggestion = React.useMemo(() => {
    if (jobData?.title) {
      return {
        title: t('optimizer.nextActionModal.oneStepCloser'),
        body: t('optimizer.nextActionModal.oneStepCloserBody'),
        recommendedId: 'prepare-for-interview' as NextActionPage,
      };
    }
    return {
      title: t('optimizer.nextActionModal.whatsNext'),
      body: t('optimizer.nextActionModal.whatsNextBody'),
      recommendedId: 'ai-companion' as NextActionPage,
    };
  }, [jobData?.title, t]);
  const atsScore = optimizationResult?.afterScores?.overall ?? optimizationResult?.optimizedScore ?? 0;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop — covers sidebar, header, everything */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-md"
            style={{ pointerEvents: 'all' }}
            aria-hidden
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 pointer-events-none"
          >
            <div
              className="vscode-bg-secondary pointer-events-auto flex w-full max-w-2xl cursor-default flex-col overflow-hidden rounded-t-2xl border border-solid shadow-xl sm:rounded-2xl vscode-border"
              style={{ maxHeight: 'min(85vh, calc(100dvh - env(safe-area-inset-top,0px) - env(safe-area-inset-bottom,0px) - 16px))' }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              {/* ── Title bar ── */}
              <div
                className="flex flex-shrink-0 items-center gap-3 px-5 py-4"
                style={{ backgroundColor: 'var(--brand-popup-header)' }}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/30 bg-white/20 shadow-sm">
                  <Zap className="h-5 w-5 text-white" fill="white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="mb-1 text-[10px] font-bold uppercase leading-none tracking-[0.15em] text-white/80">
                    Resume Optimizer
                  </p>
                  <h2 className="truncate text-base font-bold leading-tight text-white">
                    [{t('optimizer.nextActionModal.title', 'What would you like to do next?')}]
                  </h2>
                  <p className="mt-1 line-clamp-2 text-xs leading-snug text-white/75">
                    {t(
                      'optimizer.nextActionModal.subtitle',
                      'Choose where to go next — interviews, companion, dashboard, or another optimization.',
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-white/60 hover:text-white hover:bg-white/15 transition-colors touch-manipulation shrink-0"
                  aria-label={t('optimizer.nextActionModal.close')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Actions + suggestion: all scrollable in single column on mobile, two columns on md+ */}
              <div className="flex-1 min-h-0 overflow-y-auto md:overflow-hidden md:grid md:grid-cols-[1fr_0.7fr] md:gap-0">
                {/* Actions list */}
                <div className="p-3 sm:p-4 space-y-1.5 sm:space-y-1 md:overflow-y-auto">
                  {actions.map((action) => {
                    const Icon = action.icon;
                    const isRecommended = action.id === suggestion.recommendedId;
                    return (
                      <button
                        key={action.id}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onAction(action.id);
                        }}
                        className={`flex min-h-[52px] w-full cursor-pointer touch-manipulation items-center gap-3 rounded-xl px-3 py-3 text-left transition-all sm:min-h-0 sm:py-2.5 ${
                          isRecommended
                            ? 'border border-[#6C47FF]/30 bg-[#6C47FF]/10 dark:border-[#5B8DFF]/30 dark:bg-[#5B3FFF]/20'
                            : 'border border-transparent hover:opacity-90'
                        }`}
                      >
                        <div
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg sm:h-9 sm:w-9 ${
                            isRecommended
                              ? 'bg-indigo-600 dark:bg-[#5B8DFF]'
                              : 'vscode-bg-tertiary'
                          }`}
                        >
                          <Icon
                            className={`h-5 w-5 sm:h-4 sm:w-4 ${isRecommended ? 'text-white' : 'vscode-text-secondary'}`}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span
                            className={`block text-sm font-medium ${isRecommended ? 'text-[#6C47FF] dark:text-[#5B8DFF]' : 'vscode-text-primary'}`}
                          >
                            [{action.label}]
                          </span>
                          <span className="vscode-text-secondary block truncate text-xs">{action.description}</span>
                        </div>
                        <ChevronRight className="vscode-text-tertiary h-5 w-5 shrink-0 sm:h-4 sm:w-4" />
                      </button>
                    );
                  })}
                </div>

                {/* Suggestion – inline below actions on mobile, right column on md+ */}
                <div
                  className="mx-3 mb-3 flex flex-col rounded-xl border-t border-solid p-3 md:mx-0 md:mb-0 md:max-h-none md:overflow-y-auto md:rounded-none md:border-l md:border-t-0 vscode-border md:p-4"
                  style={{ background: 'rgba(108,71,255,0.04)', paddingBottom: 'max(12px, env(safe-area-inset-bottom,0px))' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 shrink-0" style={{ color: '#6C47FF' }} />
                    <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#6C47FF' }}>{t('optimizer.nextActionModal.weSuggest')}</span>
                  </div>
                  <h3 className="vscode-text-primary mb-1 text-sm font-semibold">{suggestion.title}</h3>
                  <p className="vscode-text-secondary mb-2 text-xs">{suggestion.body}</p>
                  {atsScore > 0 && (
                    <div className="vscode-text-secondary border-t border-solid pt-2 text-xs vscode-border">
                      {t('optimizer.nextActionModal.yourAtsScore', { score: Math.round(atsScore) })}
                      {jobData?.title && (
                        <span className="block mt-0.5 truncate" title={jobData.title}>
                          {t('optimizer.nextActionModal.for', { title: jobData.title })}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default NextActionModal;
